import React, { useState } from 'react';
import client from '../api/client';

// ── Current terms version — must match server/routes/auth.js CURRENT_TERMS_VERSION
export const CURRENT_TERMS_VERSION = '1.0';

// ── Full Terms of Service & Privacy Policy (combined document)
const TERMS_OF_SERVICE = `TERMS OF SERVICE & PRIVACY POLICY
Version ${CURRENT_TERMS_VERSION} · Effective Date: June 2026

Welcome to the School Management Platform. These Terms of Service and Privacy Policy govern the use of the platform by schools, administrators, teachers, parents, students, and other authorized users.

By accessing or using the platform, you agree to these terms.

────────────────────────────────────────

1. PLATFORM PURPOSE

The School Management Platform is designed to help schools manage academic and administrative operations, including:

  • Students
  • Teachers
  • Parents and Guardians
  • Classes and Subjects
  • Attendance
  • Homework and Assignments
  • Grades and Assessments
  • Timetables
  • Announcements
  • School Administration

The platform may be updated, modified, or improved from time to time.

────────────────────────────────────────

2. SCHOOL DATA OWNERSHIP

Each school retains ownership of its data, including:

  • Student records
  • Teacher records
  • Parent records
  • Attendance records
  • Academic records
  • School documents
  • Communications and announcements

The platform provider does not claim ownership of school data.

────────────────────────────────────────

3. ACCOUNT RESPONSIBILITIES

Schools and authorized users are responsible for:

  • Maintaining accurate information
  • Protecting usernames, passwords, and PINs
  • Managing user permissions appropriately
  • Ensuring only authorized individuals access the system

Users are responsible for activities performed under their accounts.

────────────────────────────────────────

4. INFORMATION WE PROCESS

The platform may process information necessary to provide the service, including:

School Information
  • School name
  • Contact information
  • Branding settings
  • Administrative records

User Information
  • Names
  • Phone numbers
  • Email addresses
  • Login activity
  • User roles and permissions

Student Information
  • Student profiles
  • Attendance records
  • Academic performance
  • Homework submissions
  • Timetable information

Parent and Guardian Information
  • Names
  • Contact information
  • Student relationships

────────────────────────────────────────

5. HOW INFORMATION IS USED

Information may be used to:

  • Provide and operate the platform
  • Authenticate users
  • Manage school operations
  • Generate reports and analytics
  • Improve platform functionality
  • Provide customer support
  • Maintain platform security
  • Comply with legal obligations

────────────────────────────────────────

6. PLATFORM OPERATIONS AND SUPPORT

The Platform Provider may access, use, and process information as reasonably necessary to provide, maintain, secure, support, and improve the Services.

This may include:

  • Customer support and onboarding
  • Troubleshooting technical issues
  • Maintaining platform functionality
  • Monitoring system performance and reliability
  • Investigating security incidents
  • Recovering accounts and restoring access
  • Preventing misuse or abuse of the platform
  • Complying with applicable legal obligations

Access is limited to authorized personnel and subject to appropriate security controls.

Certain support, operational, and administrative activities may be logged for security, auditing, and service improvement purposes.

────────────────────────────────────────

7. PRIVACY AND CONFIDENTIALITY

The Platform Provider is committed to protecting the privacy and confidentiality of school information.

Information is processed only as reasonably necessary to:

  • Deliver the Services
  • Support users
  • Maintain platform security
  • Improve reliability and performance
  • Meet legal and regulatory obligations

The Platform Provider does not sell school, student, parent, teacher, or administrator information to third parties.

────────────────────────────────────────

8. DATA SECURITY

Reasonable administrative, technical, and organizational safeguards are used to help protect information from unauthorized access, disclosure, misuse, loss, or alteration.

While strong security measures are maintained, no system can guarantee absolute security.

────────────────────────────────────────

9. SERVICE AVAILABILITY

The Platform Provider strives to provide reliable and secure access to the Services.

However, uninterrupted availability cannot be guaranteed. Scheduled maintenance, upgrades, internet outages, security events, and circumstances beyond reasonable control may occasionally affect service availability.

────────────────────────────────────────

10. ACCEPTABLE USE

Users may not:

  • Attempt unauthorized access
  • Circumvent security measures
  • Upload malicious software
  • Interfere with platform operations
  • Use the platform for unlawful purposes
  • Misrepresent identities, permissions, or authority

────────────────────────────────────────

11. SUSPENSION OR TERMINATION

Access may be suspended or terminated if:

  • These terms are violated
  • Unauthorized activity is detected
  • Security risks arise
  • Fraudulent or abusive behavior occurs
  • Legal or regulatory requirements require action

Schools may discontinue use of the platform at any time.

────────────────────────────────────────

12. LIMITATION OF LIABILITY

The Services are provided on an "as available" basis.

To the maximum extent permitted by applicable law, the Platform Provider shall not be liable for indirect, incidental, consequential, special, or punitive damages arising from the use of the platform.

────────────────────────────────────────

13. UPDATES TO THESE TERMS

These Terms of Service and Privacy Policy may be updated periodically.

When material changes are made, administrators may be required to review and accept the updated version before continuing to use the platform.

Continued use of the platform after updates become effective constitutes acceptance of the revised terms.

────────────────────────────────────────

14. ACCEPTANCE

By checking the acceptance box and using the platform, you confirm that:

  • You are authorized to act on behalf of your school or organization.
  • You have read and agree to these Terms of Service and Privacy Policy.
  • You understand how information is processed and protected.
  • You agree to use the platform in accordance with these terms.

Version ${CURRENT_TERMS_VERSION}
© AddisCore School Management Platform`;

// ── Privacy Policy is the same combined document (shown separately for clarity)
const PRIVACY_POLICY = TERMS_OF_SERVICE;

// ── Document viewer modal (Terms or Privacy Policy)
function DocumentViewer({ title, content, onClose }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10001,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        background: '#fff', borderRadius: 16,
        width: '100%', maxWidth: 680, maxHeight: '85vh',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 24px 80px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: '1px solid #E5E7EB',
          flexShrink: 0,
        }}>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{title}</div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8, border: 'none',
              background: '#F3F4F6', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 18, color: '#6B7280', fontWeight: 700,
            }}
          >×</button>
        </div>
        {/* Content */}
        <div style={{
          flex: 1, overflowY: 'auto', padding: '20px 24px',
          fontSize: 13, lineHeight: 1.7, color: '#374151',
          whiteSpace: 'pre-wrap', fontFamily: 'monospace',
        }}>
          {content}
        </div>
        {/* Footer */}
        <div style={{
          padding: '14px 24px', borderTop: '1px solid #E5E7EB',
          display: 'flex', justifyContent: 'flex-end', flexShrink: 0,
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '9px 24px', borderRadius: 8, border: 'none',
              background: '#1A73E8', color: '#fff',
              fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Terms & Agreements Modal
export default function TermsModal({ onAccepted, onLogout }) {
  const [checked, setChecked]       = useState(false);
  const [accepting, setAccepting]   = useState(false);
  const [error, setError]           = useState('');
  const [viewer, setViewer]         = useState(null); // 'tos' | 'pp' | null

  const handleAccept = async () => {
    if (!checked) return;
    setAccepting(true);
    setError('');
    try {
      await client.post('/auth/accept-terms');
      onAccepted();
    } catch (err) {
      setError('Failed to record acceptance. Please try again.');
    } finally {
      setAccepting(false);
    }
  };

  return (
    <>
      {/* Document viewer overlay */}
      {viewer === 'tos' && (
        <DocumentViewer
          title="Terms of Service"
          content={TERMS_OF_SERVICE}
          onClose={() => setViewer(null)}
        />
      )}
      {viewer === 'pp' && (
        <DocumentViewer
          title="Privacy Policy"
          content={PRIVACY_POLICY}
          onClose={() => setViewer(null)}
        />
      )}

      {/* Main modal backdrop */}
      <div style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(15,23,42,0.75)',
        backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
      }}>
        <div style={{
          background: '#fff', borderRadius: 20,
          width: '100%', maxWidth: 520,
          boxShadow: '0 32px 100px rgba(0,0,0,0.35)',
          overflow: 'hidden',
          animation: 'termsSlideIn 0.25s ease',
        }}>
          {/* Top accent bar */}
          <div style={{
            height: 4,
            background: 'linear-gradient(90deg, #1A73E8, #34D399)',
          }} />

          {/* Header */}
          <div style={{ padding: '32px 36px 0' }}>
            {/* Logo / icon */}
            <div style={{
              width: 52, height: 52, borderRadius: 14,
              background: 'linear-gradient(135deg, #1A73E8, #0EA5E9)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 26, marginBottom: 20,
              boxShadow: '0 4px 16px rgba(26,115,232,0.3)',
            }}>
              📋
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#111827', marginBottom: 8 }}>
              Terms &amp; Agreements
            </div>
            <div style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.6 }}>
              Before continuing, please review and accept the AddisCore Terms of Service and Privacy Policy.
            </div>
          </div>

          {/* Body */}
          <div style={{ padding: '24px 36px' }}>
            {/* Info card */}
            <div style={{
              background: '#F0F9FF', border: '1px solid #BAE6FD',
              borderRadius: 12, padding: '14px 16px', marginBottom: 24,
              display: 'flex', gap: 12, alignItems: 'flex-start',
            }}>
              <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>ℹ️</span>
              <div style={{ fontSize: 13, color: '#0369A1', lineHeight: 1.6 }}>
                You are not required to open or read the documents to accept. Clicking the links below opens them for optional review.
              </div>
            </div>

            {/* Document links */}
            <div style={{
              background: '#F9FAFB', border: '1px solid #E5E7EB',
              borderRadius: 12, overflow: 'hidden', marginBottom: 24,
            }}>
              <button
                onClick={() => setViewer('tos')}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: '1px solid #E5E7EB', textAlign: 'left',
                  transition: 'background .12s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: '#DBEAFE', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, flexShrink: 0,
                  }}>📄</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Terms of Service</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 1 }}>Version {CURRENT_TERMS_VERSION} · Click to read</div>
                  </div>
                </div>
                <span style={{ fontSize: 18, color: '#1A73E8' }}>↗</span>
              </button>

              <button
                onClick={() => setViewer('pp')}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '14px 18px', background: 'none', border: 'none', cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background .12s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#F3F4F6'}
                onMouseLeave={e => e.currentTarget.style.background = 'none'}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: '#D1FAE5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 16, flexShrink: 0,
                  }}>🔒</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>Privacy Policy</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 1 }}>Version {CURRENT_TERMS_VERSION} · Click to read</div>
                  </div>
                </div>
                <span style={{ fontSize: 18, color: '#1A73E8' }}>↗</span>
              </button>
            </div>

            {/* Checkbox */}
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              cursor: 'pointer', marginBottom: 24,
              padding: '14px 16px',
              background: checked ? '#F0FDF4' : '#FAFAFA',
              border: `1.5px solid ${checked ? '#86EFAC' : '#E5E7EB'}`,
              borderRadius: 12, transition: 'all .15s',
            }}>
              <div style={{ position: 'relative', flexShrink: 0, marginTop: 1 }}>
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={e => setChecked(e.target.checked)}
                  style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                />
                <div style={{
                  width: 20, height: 20, borderRadius: 6,
                  border: `2px solid ${checked ? '#16A34A' : '#D1D5DB'}`,
                  background: checked ? '#16A34A' : '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'all .15s',
                }}>
                  {checked && (
                    <svg width="12" height="9" viewBox="0 0 12 9" fill="none">
                      <path d="M1 4L4.5 7.5L11 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </div>
              </div>
              <span style={{ fontSize: 14, color: '#374151', lineHeight: 1.5, fontWeight: 500 }}>
                I have read and agree to the{' '}
                <span
                  onClick={e => { e.preventDefault(); e.stopPropagation(); setViewer('tos'); }}
                  style={{ color: '#1A73E8', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Terms of Service
                </span>
                {' '}and{' '}
                <span
                  onClick={e => { e.preventDefault(); e.stopPropagation(); setViewer('pp'); }}
                  style={{ color: '#1A73E8', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Privacy Policy
                </span>
                .
              </span>
            </label>

            {/* Error */}
            {error && (
              <div style={{
                background: '#FEF2F2', border: '1px solid #FECACA',
                borderRadius: 10, padding: '10px 14px', marginBottom: 16,
                fontSize: 13, color: '#DC2626', display: 'flex', alignItems: 'center', gap: 8,
              }}>
                ⚠️ {error}
              </div>
            )}

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={onLogout}
                style={{
                  flex: 1, padding: '12px 20px', borderRadius: 10,
                  border: '1.5px solid #E5E7EB', background: '#fff',
                  color: '#374151', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  transition: 'all .12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#F9FAFB'; e.currentTarget.style.borderColor = '#D1D5DB'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
              >
                Logout
              </button>
              <button
                onClick={handleAccept}
                disabled={!checked || accepting}
                style={{
                  flex: 2, padding: '12px 20px', borderRadius: 10, border: 'none',
                  background: checked
                    ? 'linear-gradient(135deg, #1A73E8, #0EA5E9)'
                    : '#E5E7EB',
                  color: checked ? '#fff' : '#9CA3AF',
                  fontSize: 14, fontWeight: 700,
                  cursor: checked && !accepting ? 'pointer' : 'not-allowed',
                  transition: 'all .15s',
                  boxShadow: checked ? '0 4px 14px rgba(26,115,232,0.35)' : 'none',
                }}
              >
                {accepting ? 'Saving…' : 'Accept & Continue →'}
              </button>
            </div>
          </div>

          {/* Footer note */}
          <div style={{
            padding: '12px 36px 20px',
            fontSize: 11, color: '#9CA3AF', textAlign: 'center', lineHeight: 1.5,
          }}>
            Your acceptance is recorded with a timestamp and agreement version for compliance purposes.
          </div>
        </div>
      </div>

      {/* Slide-in animation */}
      <style>{`
        @keyframes termsSlideIn {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)    scale(1);    }
        }
      `}</style>
    </>
  );
}
